import React, { useContext, useState, useRef, useEffect } from "react";
import { X, AlertCircle } from "react-feather";
import AppContext from "../../store/app-context";
import Modal from "../UI/Modal";
import Input from "../UI/Input";
import { buildAddCartByQuoteUrl } from "../../util/addCartByQuoteUrl";
import styles from "./QuoteHistoryNewBrand.module.css";

const RedeemQuoteModal = ({ initialQuoteNumber = '', onClose }) => {
  const appCtx = useContext(AppContext);
  const [redeemQuoteNumber, setRedeemQuoteNumber] = useState(initialQuoteNumber);
  const [redeemError, setRedeemError] = useState(null);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const defaults = {
    redeem_quote_title: 'Redeem quote',
    redeem_quote_desc: 'Enter your Bio-Rad quote number to redeem it. All items from the quote will be added to your cart at their quoted prices. From there, you can complete your purchase.',
    redeem_quote_placeholder: 'Quote Number',
    add_quote_to_cart: 'Add quote to cart',
    redeem_error_already_redeemed: 'The shopping cart is already on this quote.',
    redeem_error_invalid: 'The quote ID does not exist. Please try a different quote ID.',
    redeem_error_cart_conflict: 'Please remove items in cart to add this quote',
  };

  let {
    redeem_quote_title,
    redeem_quote_desc,
    redeem_quote_placeholder,
    add_quote_to_cart,
    redeem_error_already_redeemed,
    redeem_error_invalid,
    redeem_error_cart_conflict
  } = defaults;

  if (appCtx?.bioradResources) {
    redeem_quote_title = appCtx.bioradResources.RedeemQuoteTitle ?? redeem_quote_title;
    redeem_quote_desc = appCtx.bioradResources.RedeemQuoteDesc ?? redeem_quote_desc;
    redeem_quote_placeholder = appCtx.bioradResources.RedeemQuotePlaceholder ?? redeem_quote_placeholder;
    add_quote_to_cart = appCtx.bioradResources.AddQuoteToCart ?? add_quote_to_cart;
    redeem_error_already_redeemed = appCtx.bioradResources.RedeemErrorAlreadyRedeemed ?? redeem_error_already_redeemed;
    redeem_error_invalid = appCtx.bioradResources.RedeemErrorInvalid ?? redeem_error_invalid;
    redeem_error_cart_conflict = appCtx.bioradResources.RedeemErrorCartConflict ?? redeem_error_cart_conflict;
  }

  const handleRedeemInputChange = (e) => {
    setRedeemQuoteNumber(e.target.value);
    if (redeemError) setRedeemError(null);
  };

  const handleAddQuoteToCart = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const trimmedQuoteNumber = redeemQuoteNumber.trim();
    if (!trimmedQuoteNumber || redeemError || isRedeeming) return;

    if (localStorage.getItem('quoteId')) {
      setRedeemError('alreadyRedeemed');
      return;
    }

    if (!appCtx?.envProp?.appServer) {
      setRedeemError('invalid');
      return;
    }

    setIsRedeeming(true);
    setRedeemError(null);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const redeemQuoteURL = `/etlapi/redeem-quote/${encodeURIComponent(trimmedQuoteNumber)}?country=${appCtx.country}&currency=USD`;
      const response = await fetch(redeemQuoteURL, { credentials: 'include', signal: abortController.signal });

      if (!response.ok) {
        setRedeemError('invalid');
        setIsRedeeming(false);
        return;
      }

      const quoteData = await response.json();

      if (!quoteData || quoteData.status === 'fail') {
        setRedeemError('invalid');
        setIsRedeeming(false);
        return;
      }

      const isFreightQuote = quoteData.freightDiscount > 0 || quoteData.packagingDiscount > 0;
      const hasOptionalItems = quoteData.optionalItems?.length > 0;
      const cartQuantity = Number(localStorage.getItem('cart-quantity')) || 0;

      if ((isFreightQuote || hasOptionalItems) && cartQuantity > 0) {
        setRedeemError('cartConflict');
        setIsRedeeming(false);
        return;
      }

      setIsRedeeming(false);
      window.location.href = buildAddCartByQuoteUrl(appCtx, trimmedQuoteNumber);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Failed to redeem quote:', err);
      setRedeemError('invalid');
      setIsRedeeming(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className={styles.redeemModalContainer}>
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close"
        >
          <X height={24} width={24} />
        </button>
        <h2 className={styles.mainRedeemTitle}>{redeem_quote_title}</h2>
        <div className={styles.mainRedeemDescription}>{redeem_quote_desc}</div>
        <form onSubmit={handleAddQuoteToCart}>
          <Input
            className={styles.redeemQuoteInput}
            placeholder={redeem_quote_placeholder}
            input={{
              id: "redeemQuoteNumber",
              type: "text",
              name: "redeemQuoteNumber",
              value: redeemQuoteNumber,
              onChange: handleRedeemInputChange,
            }}
          />
          {redeemError === 'alreadyRedeemed' && (
            <p className={styles.redeemerrormsg}>
              <span>
                {redeem_error_already_redeemed}
              </span>
            </p>
          )}
          {redeemError === 'invalid' && (
            <p className={styles.redeemerrormsg}>
              <span>{redeem_error_invalid}</span>
            </p>
          )}
          {redeemError === 'cartConflict' && (
            <p className={styles.redeemerrormsg}>
              <span>{redeem_error_cart_conflict}</span>
            </p>
          )}
          <button
            type="submit"
            className={styles.modalPrimaryButton}
            disabled={!redeemQuoteNumber.trim() || !!redeemError || isRedeeming}
          >
            {add_quote_to_cart}
          </button>
        </form>
      </div>
    </Modal>
  );
};

export default RedeemQuoteModal;
